import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { get, post, remove } from 'src/api/requests';
import { useEnvironment } from './EnvironmentContext';
import { useSessionStorageState } from 'ahooks';
import { getAuth } from 'firebase/auth';
import { Loading } from 'src/components/Loading';

export type ServiceModeContext = {
  userId: string;
  envId: string;
};

type ServiceModeContextType = {
  generateServiceKey: (environmentId: string, orgId: string) => Promise<string>;
  revokeServiceKey: () => Promise<void>;
  serviceKey?: string;
  overrideEnvId?: string;
};

type ServiceModeProviderProps = {
  children: React.ReactNode;
};

const ServiceModeContext = createContext<ServiceModeContextType>(
  {} as ServiceModeContextType,
);

type CreateApiKeyResponse = {
  id: string;
  apiKey: string;
};

type ServiceModeApiKeyResponse = {
  id: string;
  apiKey: string;
  environment: { id: string };
};

export const ServiceModeProvider = ({ children }: ServiceModeProviderProps) => {
  const [serviceKeyId, setServiceKeyId] = useSessionStorageState<string>(
    '@control-plane/service-key-id',
    {
      defaultValue: '',
      listenStorageChange: true,
    },
  );
  const [serviceKey, setServiceKey] = useState<string>();
  const [overrideEnvId, setOverrideEnvId] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const { environment } = useEnvironment();
  const auth = getAuth();

  const fetchServiceKey = useCallback(async (keyId: string) => {
    try {
      setLoading(true);
      const data: ServiceModeApiKeyResponse = await get(
        `/api-key/${keyId}/service-mode`,
      );
      setServiceKey(data.apiKey);
      setOverrideEnvId(data.environment.id);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!serviceKeyId) {
      return;
    }
    if (!auth.currentUser) {
      return;
    }
    fetchServiceKey(serviceKeyId);
  }, [serviceKeyId, auth.currentUser, fetchServiceKey]);

  const revokeServiceKey = useCallback(async () => {
    if (!serviceKeyId) {
      return;
    }
    await remove(`/api-key/${serviceKeyId}/service-mode`, {
      envId: environment?.envId,
    });
    setServiceKey(undefined);
    setOverrideEnvId(undefined);
    setServiceKeyId(undefined);
  }, [environment?.envId, serviceKeyId, setServiceKeyId]);

  const generateServiceKey = useCallback(
    async (environmentId: string, orgId: string) => {
      // Only allow one at a time
      if (serviceKeyId) {
        await revokeServiceKey();
      }
      const data: CreateApiKeyResponse = await post(
        `/api-key`,
        { environmentId, orgId },
        {
          envId: environment?.envId,
        },
      );

      setServiceKey(data.apiKey);
      setServiceKeyId(data.id);
      setOverrideEnvId(environmentId);
      return data.apiKey;
    },
    [environment?.envId, revokeServiceKey, serviceKeyId, setServiceKeyId],
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <ServiceModeContext.Provider
      value={{
        generateServiceKey,
        revokeServiceKey,
        serviceKey,
        overrideEnvId,
      }}
    >
      {children}
    </ServiceModeContext.Provider>
  );
};

export const useServiceMode = () => {
  return useContext(ServiceModeContext);
};
